import type { GameplayTunables } from '../../shared/types/tunables';

/**
 * Every playtest-tunable constant lives here, never in scene code (D-010).
 * Sources: MVP notes §12 (timers), §14 (shards and bonuses), §7 (participation grace);
 * game design spec §14 (portal dwell, 500–800 ms); D-011 (third-interaction shard value).
 */
export const DEFAULT_GAMEPLAY_TUNABLES: GameplayTunables = {
  shards: {
    first: 3,
    retry: 2,
    afterTeaching: 1,
    thirdInteraction: 1,
  },
  teamBonus: {
    coopAction: 1,
    missionComplete: 3,
  },
  timersMs: {
    repeatInstruction: 8_000,
    visualHint: 15_000,
    teachingOrOverride: 25_000,
    // Long enough that a five-year-old sees the beginning and the celebration, short enough that
    // an impatient one is not held by it — and skippable either way (R1-12, GLP-10).
    narrativeBeat: 2_500,
  },
  participationGraceMs: {
    zoneOrBinary: 1_000,
    countAction: 1_500,
  },
  // Invented values: no source document names either window (COOP-06, D-045 PS2-4). Chosen so a
  // five-year-old who looks up, sees their sibling posing and copies them still counts as having
  // joined in, and so the whole moment cannot outlast a young child's patience: at worst it is
  // the beat, the window and one nudge. Neither can fail anybody — Phase 6 playtest candidates.
  bothRequiredMs: {
    completionWindow: 3_000,
    encouragementWindow: 2_500,
  },
  portalDwellMs: 650,
  countActionSettleMs: 900,
  detector: {
    // Placeholders until Phase 3 measures real detector behaviour (D-019).
    actionDebounceMs: 250,
    actionCooldownMs: 400,
    zoneChangeDebounceMs: 200,
  },
  // Invented placeholders, ratified by D-060(5) from the Slice 0 Mac M1 measurements: ~11 ms per
  // GPU frame (~90 fps), ~7.3 s cold first inference, 0.91–0.93 core confidence on a clean
  // detection against 0.55–0.78 on difficult joints. No document names any of these; every one is
  // a Phase 6 tuning candidate (D-010/D-023), not a requirement.
  pose: {
    poseTargetFps: 30,
    lowPerformanceFrameMs: 60,
    poseWarmupDetections: 1,
    trackingLossPauseMs: 600,
    minCoreConfidence: 0.6,
    // Slice B addition, same discipline: no document names a stillness duration.
    stillnessHoldMs: 500,
    // Slice E additions, same discipline again. `trackingReacquireMs` is the debounce PS3-3 named
    // and nobody had built; the other two are PS3-6's performance numbers, seeded from the Slice 0
    // Mac M1 spike. ~8 frames of the body back in view; half cadence when the machine struggles;
    // five seconds of a degraded session before a grown-up is offered the keyboard.
    trackingReacquireMs: 250,
    lowPerformanceTargetFps: 15,
    fallbackOfferMs: 5_000,
    // Slice G addition, same discipline: no document names a disconnection threshold. Two seconds
    // is ~60 frames at the target cadence — far longer than any stall the low-performance path
    // already handles honestly, short enough that a family is not left playing at a dead camera.
    frameStarvationMs: 2_000,
    // Round-5 addition, same discipline: no document names an observation bound. Four hundred
    // milliseconds is ~12 frames at the target cadence and ~6 at the low-performance floor, and it
    // is the one definition of "the run of frames ended" — read by every lane adapter, every
    // calibration flow and the lane resolver, from the tunables each is handed (D-090, S6-7).
    observationGapMs: 400,
  },
};
